import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useNavigate, useParams } from 'react-router-dom';
import { Field, Input, Label, Button, Select } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import ConfirmModal from '../shared/ConfirmModal';
import * as userService from '../../services/userService';
import type { UserDto } from '../../services/userService';
import { getAllDomainConfigs, ApiDomainConfig } from '../../services/domainConfigService';

const AddUser: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const { id } = useParams<{ id: string }>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [availableDomains, setAvailableDomains] = useState<ApiDomainConfig[]>([]);
  const [selectedDomainIds, setSelectedDomainIds] = useState<number[]>([]);

  // Fetch available domain configs for domain_admin assignment
  useEffect(() => {
    getAllDomainConfigs()
      .then((configs) => setAvailableDomains(configs))
      .catch((err) => console.error('Error fetching domain configs:', err));
  }, []);

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      role: 'merchant',
      languagePreference: 'en',
    },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      try {
        const payload: UserDto = {
          ...values as UserDto,
          ...(values.role === 'domain_admin' ? { domainConfigIds: selectedDomainIds } : {}),
        };

        if (isEditing && id) {
          await userService.updateUser(parseInt(id), payload);
        } else {
          await userService.createUser(payload);
        }
        setError('');
        navigate('/users');
      } catch (error) {
        setError('Error saving user.');
        console.error('Error submitting form:', error);
      }
    },
  });

  // Fetch user data if editing
  useEffect(() => {
    if (id) {
      setIsEditing(true);

      userService
        .getUserById(parseInt(id))
        .then((user) => {
          formik.setValues({
            name: user.name || '',
            email: user.email || '',
            role: user.role || 'merchant',
            languagePreference: user.languagePreference || 'en',
          });
          if (user.assignedDomains) {
            setSelectedDomainIds(user.assignedDomains.map((d) => d.id));
          }
        })
        .catch((error) => {
          setError('Error fetching user.');
          console.error('Error fetching user details:', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const confirmDelete = (id: number) => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (userId: number) => {
    if (id) {
      try {
        await userService.deleteUser(userId);
        navigate('/users');
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
    setShowDeleteModal(false);
  };

  const handleDomainToggle = (domainId: number) => {
    setSelectedDomainIds((prev) =>
      prev.includes(domainId)
        ? prev.filter((id) => id !== domainId)
        : [...prev, domainId],
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">{isEditing ? t('users.update') : t('users.add')}</h1>
        <p className="text-slate-600 mt-1">
          {isEditing ? t('users.updateUserSubtitle') : t('users.createUserSubtitle')}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Form Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <form onSubmit={formik.handleSubmit} className="space-y-6">
          {/* Name Field */}
          <Field>
            <Label className="block text-sm font-medium text-slate-700 mb-2">{t('users.name')}</Label>
            <Input
              id="name"
              type="text"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required
              className={clsx(
                'block w-full rounded-lg border border-slate-300 bg-white py-2.5 px-4 text-slate-900',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                'placeholder:text-slate-400'
              )}
            />
          </Field>

          {/* Email Field */}
          <Field>
            <Label className="block text-sm font-medium text-slate-700 mb-2">{t('users.email')}</Label>
            <Input
              id="email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required
              className={clsx(
                'block w-full rounded-lg border border-slate-300 bg-white py-2.5 px-4 text-slate-900',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                'placeholder:text-slate-400'
              )}
            />
          </Field>

          {/* Role Field */}
          <Field>
            <Label className="block text-sm font-medium text-slate-700 mb-2">{t('users.role')}</Label>
            <div className="relative">
              <Select
                id="role"
                value={formik.values.role}
                onChange={formik.handleChange}
                required
                className={clsx(
                  'block w-full appearance-none rounded-lg border border-slate-300 bg-white py-2.5 px-4 pr-10 text-slate-900',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  '*:text-slate-900'
                )}
              >
                <option value="admin">Admin</option>
                <option value="domain_admin">Domain Admin</option>
                <option value="merchant">Merchant</option>
              </Select>
              <ChevronDownIcon
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-5 text-slate-400"
                aria-hidden="true"
              />
            </div>
          </Field>

          {/* Domain Assignment — shown only when role is domain_admin */}
          {formik.values.role === 'domain_admin' && (
            <Field>
              <Label className="block text-sm font-medium text-slate-700 mb-2">
                Assigned Domains
              </Label>
              <p className="text-slate-500 text-sm mb-3">
                Select which domains this admin can manage.
              </p>
              <div className="space-y-2">
                {availableDomains.map((domain) => (
                  <label
                    key={domain.id}
                    className={clsx(
                      'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                      selectedDomainIds.includes(domain.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDomainIds.includes(domain.id)}
                      onChange={() => handleDomainToggle(domain.id)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-medium text-slate-900">{domain.brandName}</span>
                      <span className="text-slate-500 text-sm ml-2">({domain.hostname})</span>
                    </div>
                  </label>
                ))}
                {availableDomains.length === 0 && (
                  <p className="text-slate-400 text-sm italic">Loading domains...</p>
                )}
              </div>
            </Field>
          )}

          {/* Language Preference Field */}
          <Field>
            <Label className="block text-sm font-medium text-slate-700 mb-2">{t('users.languagePreference')}</Label>
            <div className="relative">
              <Select
                id="languagePreference"
                value={formik.values.languagePreference}
                onChange={formik.handleChange}
                required
                className={clsx(
                  'block w-full appearance-none rounded-lg border border-slate-300 bg-white py-2.5 px-4 pr-10 text-slate-900',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  '*:text-slate-900'
                )}
              >
                <option value="en">{t('languages.en')}</option>
                <option value="de">{t('languages.de')}</option>
                <option value="es">{t('languages.es')}</option>
                <option value="it">{t('languages.it')}</option>
              </Select>
              <ChevronDownIcon
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-5 text-slate-400"
                aria-hidden="true"
              />
            </div>
          </Field>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              className="inline-flex items-center px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-sm hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {isEditing ? t('users.update') : t('users.add')}
            </Button>

            {isEditing && (
              <Button
                type="button"
                onClick={() => confirmDelete(Number(id))}
                className="inline-flex items-center px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl shadow-sm hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                {t('common.delete')}
              </Button>
            )}

            <Button
              type="button"
              onClick={() => navigate('/users')}
              className="inline-flex items-center px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
            >
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => handleConfirmDelete(Number(id))}
          title={t('users.confirmDeletion')}
          message={t('users.confirmDeletionMessage')}
        />
      )}
    </div>
  );
};

export default AddUser;
